import { useEffect, useMemo, useState } from 'react';
import { fetchDeviceEventsCountV2 } from '@/entities/strip';
import { EMITTER_CONSTANTS, KEY_RECOIL, DEVICE_TAB_TYPES } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { logError } from '@/shared/lib/apiHelpers';
import { clearApolloCache } from '@/shared/api';
import emitter from '@/shared/lib/eventEmitter';
import { selectActiveTab, selectDeviceEventCount, useReportStore } from '@/entities/report';
import type { DeviceEventWrapperProps } from './index';

/**
 * Coordinates the device events tab, tracking the per-type event counts and whether the active type has any data. Reads the ECG data map, report info, active sub-tab and cached counts from the report store, refetches the counts whenever the study, report or tab activation changes and on update or reload signals, and derives the empty flag from the count for the active device tab.
 *
 * @param props - Wrapper props with the report data and whether the tab is currently active.
 * @returns An object carrying the tab store key, active sub-tab, ECG data map, study id, timezone offset, loading and header flags, the empty-data flag and the refresh handler.
 */
const useDeviceEventWrapper = ({ reportData, isActive }: DeviceEventWrapperProps) => {
  const keyRecoil = KEY_RECOIL.DEVICE_MANAGEMENT;
  const activeButton = useReportStore(selectActiveTab(KEY_RECOIL.STRIP_MANAGEMENT));
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const deviceEventCount = useReportStore(selectDeviceEventCount(keyRecoil));
  const setDeviceEventCountAction = useReportStore((s) => s.actions.setDeviceEventCount);
  /**
   * Writes the device event counts for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New count map, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setDeviceEventCount = (v: Parameters<typeof setDeviceEventCountAction>[1]) =>
    setDeviceEventCountAction(keyRecoil, v);
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { studyId, profileId, startReport, timezoneOffset } = useReportStore(
    (s) => s.reportInfo,
  ) as any;
  const [loading, setLoading] = useState(false);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);

  /**
   * Loads the per-type device event counts for the current study and report. Raises the loading flag, queries the counts through the cached V2 fetch, stores them on the tab, logs any failure and always clears loading again.
   *
   * @returns A promise resolving once the counts have been stored or the failure logged.
   */
  const fetchData = async () => {
    setLoading(true);
    try {
      const filter = {
        studyId,
        reportId: reportData?.report?.id,
      };
      const data = await fetchDeviceEventsCountV2(filter, true);
      setDeviceEventCount(data);
    } catch (error) {
      logError('Failed to fetch device events count: ', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refreshes the device event counts in response to an update or reload signal. Fetches only while the tab is active.
   *
   * @returns Nothing.
   */
  const handleReloadCount = () => {
    if (isActive) {
      fetchData();
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchData();
    }
  }, [studyId, reportData?.report?.id, isActive]);

  /**
   * Reloads the device event strips after an empty result. Clears the Apollo cache, then emits the reload-event-strip signal so the listening tabs refetch.
   *
   * @returns A promise resolving once the Apollo cache has been cleared and the reload signal emitted.
   */
  const onClickRefreshEmptyData = async () => {
    await clearApolloCache();
    emitter.emit(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP);
  };

  const isEmptyData = useMemo(() => {
    if (DEVICE_TAB_TYPES.includes(activeButton as any)) {
      return !deviceEventCount || !(deviceEventCount as any)[activeButton];
    }
    return false;
  }, [deviceEventCount, activeButton]);

  useEmitter(EMITTER_CONSTANTS.UPDATE_EVENT_SUCCESS, handleReloadCount, [
    studyId,
    reportData?.report?.id,
    isActive,
  ]);
  useEmitter(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP, handleReloadCount, [
    studyId,
    reportData?.report?.id,
    isActive,
  ]);

  return {
    keyRecoil,
    activeButton,
    ecgDataMap,
    studyId,
    timezoneOffset,
    loading,
    isShowTabHeader,
    onClickRefreshEmptyData,
    isEmptyData,
  };
};

export default useDeviceEventWrapper;
